import { useChartStore } from '@/stores/chartStore';
import { useLayoutStore } from '@/stores/layoutStore';
import { useAuthentication } from '@/composables/useAuthentication';
import { useLoadSymbolOnly } from '@/composables/useLoadSymbolOnly';
import { useChartNotesV2 } from '@/composables/useChartNotesV2';
import { useActionResultMessages } from '@/composables/useActionResultMessages';
import { usePointAndFigureCharts } from '@/composables/usePointAndFigureCharts';
import { useUniqueIds } from '@/composables/useUniqueIds';
import { useSymbolInformation } from '@/composables/useSymbolInformation';
import { useModals } from '@/composables/useModals';
import { useWorkbenchSelections } from '@/composables/useWorkbenchSelections';
import { getSharpCharts2ChartStyle } from '@/composables/getSharpCharts2ChartStyle';
import { getChart } from '@/composables/getChart';
import { getChartStyle } from '@/composables/getChartStyle';
import { INVALID_CHART_RECID } from '@/composables/isValidChartRecid';
import { getDefaultSharpCharts2RangeStartDate } from '@/utils/getDefaultSharpCharts2RangeStartDate';
import { scrollToChart } from '@/utils/scrollToChart';
import { cloneProxyObject } from '@/utils/cloneProxyObject';
import { wait } from '@/utils/wait';
import { SimpleFrontendChart } from '@/interfaces/chart';
import { isSimpleFrontendSampleChartStyle, SimpleFrontendChartStyle } from '@/interfaces/chartStyle';
import DEFAULT_CHART from '@/default-chart.json';

let isInitialized = false;
let areLoadChartArrowHotkeysEnabled = false;

interface LoadChartStyleOptionsObject {
  shouldScrollToChartOnSuccess?: boolean;
  shouldCreateActionResultMessageOnFailure?: boolean;
  shouldThrowErrorOnFailure?: boolean;
};

interface LoadChartOptionsObject extends LoadChartStyleOptionsObject {
  shouldEnableArrowHotkeys?: boolean;
};

async function loadChart(recid: number, options: LoadChartOptionsObject = {}) {
  const chartStore = useChartStore();
  const { isActivatingAnnotationMode, isAnnotating, isDeactivatingAnnotationMode } = useChartNotesV2();
  const { addRedActionResultMessage } = useActionResultMessages();
  const { getPointAndFigureWorkbenchURL } = usePointAndFigureCharts();
  const { getSymbolInformation } = useSymbolInformation();

  if (!isDeactivatingAnnotationMode.value && (isActivatingAnnotationMode.value || isAnnotating.value)) {
    return;
  }

  const { shouldScrollToChartOnSuccess, shouldCreateActionResultMessageOnFailure, shouldThrowErrorOnFailure } = options;
  const latestLoadChartTimeStamp = chartStore.latestLoadChartTimeStamp;
  const latestLoadChartStyleTimeStamp = chartStore.latestLoadChartStyleTimeStamp;

  try {
    const chart = await getChart(recid);

    if (
      latestLoadChartTimeStamp !== chartStore.latestLoadChartTimeStamp ||
      latestLoadChartStyleTimeStamp !== chartStore.latestLoadChartStyleTimeStamp ||
      (!isDeactivatingAnnotationMode.value && (isActivatingAnnotationMode.value || isAnnotating.value))
    ) {
      return;
    }

    if (!chart) {
      throw new Error(`No chart found with recid: ${recid}.`);
    }

    const { symbol, styleCacheId, styleCacheSettingsOverrides } = chart;
    const styleCacheSettingsOverridesCopy = cloneProxyObject(styleCacheSettingsOverrides);
    const pointAndFigureWorkbenchURL = getPointAndFigureWorkbenchURL(chart);

    if (pointAndFigureWorkbenchURL) {
      window.open(pointAndFigureWorkbenchURL, '_self');
      return;
    }

    const [
      chartStoreSettings
    ] = await Promise.all([
      getSharpCharts2ChartStyle(styleCacheId),
      getSymbolInformation(symbol).catch((e) => {
        window.sc3WorkbenchLogger.debug(`Error occurred while getting symbol information for symbol: ${symbol} while loading chart with recid: ${recid}.`, e);
      })
    ]);

    if (
      latestLoadChartTimeStamp !== chartStore.latestLoadChartTimeStamp ||
      latestLoadChartStyleTimeStamp !== chartStore.latestLoadChartStyleTimeStamp ||
      (!isDeactivatingAnnotationMode.value && (isActivatingAnnotationMode.value || isAnnotating.value))
    ) {
      return;
    }

    chartStore.$patch({
      ...chartStoreSettings,
      ...styleCacheSettingsOverridesCopy,
      symbol,
      recidOfChartBeingViewed: recid,
      recidOfLoadSymbolOnlyChartBeingViewed: recid,
      latestLoadChartTimeStamp: Date.now()
    });

    if (options.shouldEnableArrowHotkeys) {
      areLoadChartArrowHotkeysEnabled = true;

      await wait(0);

      areLoadChartArrowHotkeysEnabled = true;
    }

    if (!shouldScrollToChartOnSuccess) {
      return;
    }

    await scrollToChart();
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to load chart with recid: ${recid}.`, e);

    areLoadChartArrowHotkeysEnabled = false;

    if (shouldCreateActionResultMessageOnFailure) {
      addRedActionResultMessage('Failed to load chart.');
    }

    if (shouldThrowErrorOnFailure) {
      throw new Error(`Failed to load chart with recid: ${recid}.`, { cause: e });
    }
  }
}

async function loadChartStyle(recid: number, symbol: string, options: LoadChartStyleOptionsObject = {}) {
  const chartStore = useChartStore();
  const { isActivatingAnnotationMode, isAnnotating } = useChartNotesV2();
  const { addRedActionResultMessage } = useActionResultMessages();
  const { getSymbolInformation } = useSymbolInformation();

  if (
    isActivatingAnnotationMode.value ||
    isAnnotating.value
  ) {
    return;
  }

  const { shouldScrollToChartOnSuccess, shouldCreateActionResultMessageOnFailure, shouldThrowErrorOnFailure } = options;
  const latestLoadChartTimeStamp = chartStore.latestLoadChartTimeStamp;
  const latestLoadChartStyleTimeStamp = chartStore.latestLoadChartStyleTimeStamp;

  try {
    const chartStyle = await getChartStyle(recid);

    if (
      latestLoadChartTimeStamp !== chartStore.latestLoadChartTimeStamp ||
      latestLoadChartStyleTimeStamp !== chartStore.latestLoadChartStyleTimeStamp ||
      isActivatingAnnotationMode.value ||
      isAnnotating.value
    ) {
      return;
    }

    if (!chartStyle) {
      throw new Error(`No ChartStyle found with recid: ${recid}.`);
    }

    const { styleCacheId, styleCacheSettingsOverrides } = chartStyle;
    const styleCacheSettingsOverridesCopy = cloneProxyObject(styleCacheSettingsOverrides);

    const [
      chartStoreSettings
    ] = await Promise.all([
      getSharpCharts2ChartStyle(styleCacheId),
      getSymbolInformation(symbol).catch((e) => {
        window.sc3WorkbenchLogger.debug(`Error occurred while getting symbol information for symbol: ${symbol} while loading ChartStyle with recid: ${recid}.`, e);
      })
    ]);

    if (
      latestLoadChartTimeStamp !== chartStore.latestLoadChartTimeStamp ||
      latestLoadChartStyleTimeStamp !== chartStore.latestLoadChartStyleTimeStamp ||
      isActivatingAnnotationMode.value ||
      isAnnotating.value
    ) {
      return;
    }

    chartStore.$patch({
      ...chartStoreSettings,
      ...styleCacheSettingsOverridesCopy,
      symbol,
      recidOfChartStyleBeingViewed: recid,
      latestLoadChartStyleTimeStamp: Date.now(),
      recidOfChartBeingViewed: symbol === chartStore.symbol ? chartStore.recidOfChartBeingViewed : INVALID_CHART_RECID,
      recidOfLoadSymbolOnlyChartBeingViewed: symbol === chartStore.symbol ? chartStore.recidOfLoadSymbolOnlyChartBeingViewed : INVALID_CHART_RECID
    });

    if (!shouldScrollToChartOnSuccess) {
      return;
    }

    await scrollToChart();
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to load ChartStyle with recid: ${recid}.`, e);

    if (shouldCreateActionResultMessageOnFailure) {
      addRedActionResultMessage('Failed to load ChartStyle.');
    }

    if (shouldThrowErrorOnFailure) {
      throw new Error(`Failed to load ChartStyle with recid: ${recid}.`, { cause: e });
    }
  }
}

function getPlatformDefaultChart() {
  const { getPositiveUniqueId } = useUniqueIds();
  const defaultChartCopy = cloneProxyObject(DEFAULT_CHART);

  defaultChartCopy.rangeStartDate = getDefaultSharpCharts2RangeStartDate();

  for (const indicator of defaultChartCopy.sortedIndicators) {
    indicator.id = getPositiveUniqueId();
  }

  for (const overlay of defaultChartCopy.sortedOverlays) {
    overlay.id = getPositiveUniqueId();
  }

  return defaultChartCopy;
}

async function loadPlatformDefaultChartStyle(symbol: string, options: LoadChartStyleOptionsObject = {}) {
  const chartStore = useChartStore();
  const { isActivatingAnnotationMode, isAnnotating } = useChartNotesV2();
  const { addRedActionResultMessage } = useActionResultMessages();

  if (
    isActivatingAnnotationMode.value ||
    isAnnotating.value
  ) {
    return;
  }

  const { shouldScrollToChartOnSuccess, shouldCreateActionResultMessageOnFailure, shouldThrowErrorOnFailure } = options;

  try {
    chartStore.$patch({
      ...getPlatformDefaultChart(),
      symbol,
      latestLoadChartStyleTimeStamp: Date.now(),
      recidOfChartBeingViewed: symbol === chartStore.symbol ? chartStore.recidOfChartBeingViewed : INVALID_CHART_RECID,
      recidOfLoadSymbolOnlyChartBeingViewed: symbol === chartStore.symbol ? chartStore.recidOfLoadSymbolOnlyChartBeingViewed : INVALID_CHART_RECID
    });

    if (!shouldScrollToChartOnSuccess) {
      return;
    }

    await scrollToChart();
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to load platform default ChartStyle.`, e);

    if (shouldCreateActionResultMessageOnFailure) {
      addRedActionResultMessage('Failed to load ChartStyle.');
    }

    if (shouldThrowErrorOnFailure) {
      throw new Error(`Failed to load platform default ChartStyle.`, { cause: e });
    }
  }
}

async function loadSimpleFrontendChart(chart: SimpleFrontendChart, options: LoadChartOptionsObject = {}) {
  const chartStore = useChartStore();
  const { isLoggedIn } = useAuthentication();
  const { isLoadSymbolOnlyEnabled } = useLoadSymbolOnly();
  const { launchCreateAccountModal } = useModals();

  if (!isLoggedIn.value && chart.isForPayingMembersOnly) {
    launchCreateAccountModal();
    return;
  }

  const modifiedOptions = {
    shouldScrollToChartOnSuccess: true,
    shouldCreateActionResultMessageOnFailure: true,
    ...options
  };
  const { symbol, recid } = chart;

  if (!isLoadSymbolOnlyEnabled.value) {
    await loadChart(recid, modifiedOptions);
    return;
  }

  chartStore.$patch({
    symbol,
    recidOfLoadSymbolOnlyChartBeingViewed: recid
  });

  if (modifiedOptions.shouldEnableArrowHotkeys) {
    areLoadChartArrowHotkeysEnabled = true;

    await wait(0);

    areLoadChartArrowHotkeysEnabled = true;
  }

  if (modifiedOptions.shouldScrollToChartOnSuccess) {
    await scrollToChart();
  }
}

async function loadSimpleFrontendChartStyle(chartStyle: SimpleFrontendChartStyle, options: LoadChartStyleOptionsObject = {}) {
  const chartStore = useChartStore();
  const { isLoggedIn } = useAuthentication();
  const { launchCreateAccountModal } = useModals();

  if (!isLoggedIn.value && chartStyle.isForPayingMembersOnly) {
    launchCreateAccountModal();
    return;
  }

  const modifiedOptions = {
    shouldScrollToChartOnSuccess: true,
    shouldCreateActionResultMessageOnFailure: true,
    ...options
  };

  const isSampleAndNeedsToLoadRoot = isSimpleFrontendSampleChartStyle(chartStyle) && chartStyle.rootSymbol && chartStyle.shouldLoadRootSymbol;
  const symbol = isSampleAndNeedsToLoadRoot ? chartStyle.rootSymbol : chartStore.symbol;
  await loadChartStyle(chartStyle.recid, symbol, modifiedOptions);
}

async function handleKeyDownEvent(e: KeyboardEvent) {
  const layoutStore = useLayoutStore();
  const { isActivatingAnnotationMode, isAnnotating } = useChartNotesV2();

  if (
    !areLoadChartArrowHotkeysEnabled ||
    layoutStore.modalType ||
    layoutStore.isDragNDropping ||
    isActivatingAnnotationMode.value ||
    isAnnotating.value
  ) {
    areLoadChartArrowHotkeysEnabled = false;
    return;
  }

  if (
    e.key === 'ArrowRight' ||
    e.key === 'ArrowDown' ||
    e.key === 'ArrowLeft' ||
    e.key === 'ArrowUp'
  ) {
    e.preventDefault();
  }
}

async function handleKeyUpEvent(e: KeyboardEvent) {
  const layoutStore = useLayoutStore();
  const { isActivatingAnnotationMode, isAnnotating } = useChartNotesV2();
  const { loadPreviousChart, loadNextChart } = useWorkbenchSelections();

  if (
    !areLoadChartArrowHotkeysEnabled ||
    layoutStore.modalType ||
    layoutStore.isDragNDropping ||
    isActivatingAnnotationMode.value ||
    isAnnotating.value
  ) {
    areLoadChartArrowHotkeysEnabled = false;
    return;
  }

  if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
    await loadNextChart();
  }

  if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
    await loadPreviousChart();
  }
}

function init() {
  if (isInitialized) {
    return;
  }

  document.body.addEventListener('keydown', handleKeyDownEvent);
  document.body.addEventListener('keyup', handleKeyUpEvent);
  document.body.addEventListener('click', () => { areLoadChartArrowHotkeysEnabled = false; });
  document.body.addEventListener('contextmenu', () => { areLoadChartArrowHotkeysEnabled = false; });

  isInitialized = true;
}

export function useLoadChartsAndChartStyles() {
  init();

  return {
    loadChart,
    loadChartStyle,
    getPlatformDefaultChart,
    loadPlatformDefaultChartStyle,
    loadSimpleFrontendChart,
    loadSimpleFrontendChartStyle
  };
}
